import { useMemo } from 'react';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Trans } from 'react-i18next';
import { SizeFromProps } from '../../utils/styledcomponents';
import type { SizeProps } from '../../utils/styledcomponents';
import Image from '../atoms/Image';
import Button from '../atoms/Button.Container';
import Centered from '../atoms/Centered';
import type { Concept } from '../../data/Concepts';
import Assets, { getImageByKey } from '../../assets/Assets';

const Container = styled.div<{ disabled: boolean }>`
	color: ${(props) => props.theme.colors.white};
	height: 100%;
	opacity: ${(props) => (props.disabled ? 0.5 : 1)};
	position: relative;
`;

const Symbol = styled.div`
	align-items: center;
	background-color: ${(props) => props.theme.colors.green};
	border-radius: 35%;
	box-sizing: border-box;
	color: ${(props) => props.theme.colors.black};
	display: inline-flex;
	height: 100%;
	padding: 3.2vmin;
	position: absolute;
	right: 0;
	top: 0;
`;

const Content = styled.div`
	background-color: ${(props) => props.theme.colors.darkBlue};
	border-radius: 3.8vmin;
	bottom: 3vh;
	left: 0;
	padding-bottom: 2.3vmin; /* half button height */
	padding-left: 4%;
	padding-right: calc(18vmin + 3.2vmin + 4%);
	position: absolute;
	right: 3.2vmin;
	top: 3vh;
`;

const Info = styled.div`
	align-items: stretch;
	display: flex;
	flex-flow: column nowrap;
	height: 100%;
	justify-content: center;
	overflow: hidden;
`;

const Title = styled.span`
	font: ${(props) => props.theme.font.default};
	font-size: 4.2vmin;
	letter-spacing: 0.2vmin;
	line-height: 4.8vmin;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Description = styled.div`
	display: -webkit-box;
	font: ${(props) => props.theme.font.default};
	font-size: 2.2vmin;
	height: 6vmin;
	letter-spacing: 0.1vmin;
	line-height: 3vmin;
	margin-top: 0.6vmin;
	overflow: hidden;
	text-overflow: ellipsis;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2;
`;

const Actions = styled.div`
	bottom: 0;
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	left: 0;
	max-width: 100%;
	position: absolute;
	width: 100%;
	& > * {
		margin-left: 2.5%;
	}
`;

export interface Props {
	className?: string;
	concept: Concept;
	disabled?: boolean;
	onConcept: (concept: Concept) => void;
}

const ActiveConceptItem: FunctionComponent<Props> = (props: Props) => {
	const { className, concept, disabled = false, onConcept } = props;
	const conceptImage = useMemo(
		() => getImageByKey(concept.images.icon),
		[concept]
	);

	return (
		<Container className={className} disabled={disabled}>
			<Content>
				<Info>
					<Title>
						<Trans>{concept.texts.name}</Trans>
					</Title>
					<Description>
						<Trans>{concept.texts.shortDescription}</Trans>
					</Description>
				</Info>
			</Content>
			<Symbol>
				<Image src={conceptImage} inactive width="18vmin" height="18vmin" />
				<Centered position="absolute" left="0" right="0" top="0" bottom="0">
					<Image src={Assets.images.checked} width="14vmin" height="14vmin" />
				</Centered>
			</Symbol>
			<Actions>
				<Button
					disabled={disabled}
					onClick={disabled ? undefined : () => onConcept(concept)}
				>
					<Trans>concept.find_out_more</Trans>
				</Button>
			</Actions>
		</Container>
	);
};

export default styled(ActiveConceptItem)<SizeProps>(SizeFromProps);
